import React, { useState, useCallback, useEffect } from 'react';
import { cn } from '@/lib/core/utils';
import { useKioskSession, useEmployeeAuth, useClockAction } from '@/hooks/kiosk';
import type { ClockAction, AuthMethod } from '@/hooks/kiosk';
import { PINEntryPanel } from './PINEntryPanel';
import { EmployeeStatusCard } from './EmployeeStatusCard';
import { ClockActionButtons } from './ClockActionButtons';
import { ShiftMatchDisplay } from './ShiftMatchDisplay';
import { OvertimeAlert } from './OvertimeAlert';
import QRCodeScanner from '@/components/QRCodeScanner';
import { detectQRType, extractEmployeeToken } from '../shared/QRTypeDetector';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { QrCode, KeyRound, ArrowLeft, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { toast } from '@/hooks/ui/use-toast';

type StaffMode = 'select' | 'pin' | 'qr' | 'authenticated' | 'result';
type AuthMethodUsed = AuthMethod | null;

interface StaffTimeTrackingModeProps {
  className?: string;
}

/**
 * Main staff time tracking mode component
 * Handles PIN and QR authentication, then shows status and actions
 */
export function StaffTimeTrackingMode({ className }: StaffTimeTrackingModeProps) {
  const { businessId } = useKioskSession();
  const {
    employee,
    isAuthenticating,
    error: authError,
    authenticateWithPIN,
    authenticateWithQR,
    clearEmployee,
  } = useEmployeeAuth();
  const {
    isProcessing,
    lastResult,
    performAction,
    clearResult,
  } = useClockAction();

  const [mode, setMode] = useState<StaffMode>('select');
  const [authMethodUsed, setAuthMethodUsed] = useState<AuthMethodUsed>(null);
  const [scannerActive, setScannerActive] = useState(false);

  // Auto-return to select mode after showing result
  useEffect(() => {
    if (mode === 'result' && lastResult) {
      const timeout = setTimeout(() => {
        handleReset();
      }, 3000);
      return () => clearTimeout(timeout);
    }
  }, [mode, lastResult]);

  // Switch to authenticated mode when employee is set
  useEffect(() => {
    if (employee && mode !== 'authenticated' && mode !== 'result') {
      setMode('authenticated');
    }
  }, [employee, mode]);

  const handlePINSubmit = useCallback(async (pin: string): Promise<boolean> => {
    if (!businessId) return false;
    setAuthMethodUsed('pin');
    return await authenticateWithPIN(businessId, pin);
  }, [businessId, authenticateWithPIN]);

  const handleQRScan = useCallback(async (qrData: string) => {
    if (!businessId) return;

    const detection = detectQRType(qrData);

    if (detection.type !== 'employee') {
      toast.error('Invalid QR Code', {
        description: 'Please scan your employee QR code from your phone app.',
      });
      return;
    }

    const token = extractEmployeeToken(qrData);
    if (!token) {
      toast.error('Invalid QR Code', {
        description: 'Could not read the QR code. Please try again.',
      });
      return;
    }

    setScannerActive(false);
    setAuthMethodUsed('qr');
    await authenticateWithQR(businessId, token);
  }, [businessId, authenticateWithQR]);

  const handleClockAction = useCallback(async (action: ClockAction) => {
    if (!businessId || !employee || !authMethodUsed) return;

    const result = await performAction(
      businessId,
      employee.id,
      action,
      authMethodUsed
    );

    if (result.success) {
      setMode('result');
      // Haptic feedback
      if ('vibrate' in navigator) {
        navigator.vibrate([100, 50, 100]);
      }
    } else {
      toast.error('Action Failed', {
        description: result.error || 'Please try again.',
      });
    }
  }, [businessId, employee, authMethodUsed, performAction]);

  const handleReset = useCallback(() => {
    clearEmployee();
    clearResult();
    setMode('select');
    setAuthMethodUsed(null);
    setScannerActive(false);
  }, [clearEmployee, clearResult]);

  const handleBack = useCallback(() => {
    if (mode === 'authenticated') {
      handleReset();
    } else {
      setMode('select');
      setScannerActive(false);
    }
  }, [mode, handleReset]);

  // Render based on current mode
  const renderContent = () => {
    switch (mode) {
      case 'select':
        return (
          <div className="flex flex-col items-center justify-center flex-1 gap-6 p-6">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">
              How would you like to clock in?
            </h2>

            <div className="flex gap-6">
              <Card
                className="cursor-pointer hover:border-primary transition-colors"
                onClick={() => {
                  setMode('qr');
                  setScannerActive(true);
                }}
              >
                <CardContent className="w-48 h-48 flex flex-col items-center justify-center gap-4 p-6">
                  <QrCode className="w-16 h-16 text-primary" />
                  <span className="text-gray-900 dark:text-gray-100 font-semibold text-lg">Scan QR</span>
                  <span className="text-gray-500 dark:text-gray-400 text-sm">From your phone</span>
                </CardContent>
              </Card>

              <Card
                className="cursor-pointer hover:border-green-500 transition-colors"
                onClick={() => setMode('pin')}
              >
                <CardContent className="w-48 h-48 flex flex-col items-center justify-center gap-4 p-6">
                  <KeyRound className="w-16 h-16 text-green-600 dark:text-green-400" />
                  <span className="text-gray-900 dark:text-gray-100 font-semibold text-lg">Enter PIN</span>
                  <span className="text-gray-500 dark:text-gray-400 text-sm">4-6 digit code</span>
                </CardContent>
              </Card>
            </div>
          </div>
        );

      case 'pin':
        return (
          <div className="flex flex-col items-center justify-center flex-1 p-6 relative">
            <Button
              variant="ghost"
              onClick={handleBack}
              className="absolute top-4 left-4 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back
            </Button>

            <PINEntryPanel
              onSubmit={handlePINSubmit}
              isLoading={isAuthenticating}
              error={authError}
            />
          </div>
        );

      case 'qr':
        return (
          <div className="flex flex-col items-center justify-center flex-1 p-6 relative">
            <Button
              variant="ghost"
              onClick={handleBack}
              className="absolute top-4 left-4 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 z-10"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back
            </Button>

            {isAuthenticating ? (
              <div className="text-center">
                <Loader2 className="w-12 h-12 text-primary animate-spin mx-auto mb-4" />
                <p className="text-gray-700 dark:text-gray-300 text-lg">Verifying...</p>
              </div>
            ) : scannerActive ? (
              <div className="w-full max-w-md">
                <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 text-center mb-4">
                  Scan Your Employee QR Code
                </h2>
                <QRCodeScanner
                  onScan={handleQRScan}
                  onError={(error) => {
                    console.error('Scanner error:', error);
                    toast.error('Scanner Error', { description: error });
                  }}
                  onClose={() => {
                    setScannerActive(false);
                    setMode('select');
                  }}
                  isActive={scannerActive}
                  scanType="qr"
                />
                {authError && (
                  <div className="mt-4 p-3 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/30 rounded-lg">
                    <p className="text-red-600 dark:text-red-400 text-sm text-center">{authError}</p>
                  </div>
                )}
              </div>
            ) : null}
          </div>
        );

      case 'authenticated':
        if (!employee) return null;

        return (
          <div className="flex flex-col flex-1 p-6 overflow-auto relative">
            <Button
              variant="ghost"
              onClick={handleBack}
              className="absolute top-4 left-4 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Cancel
            </Button>

            <div className="max-w-lg mx-auto w-full space-y-6 mt-8">
              {/* Employee status */}
              <EmployeeStatusCard employee={employee} />

              {/* Shift info */}
              <ShiftMatchDisplay shift={employee.current_shift} />

              {/* Overtime warning */}
              <OvertimeAlert warning={employee.overtime_warning} />

              {/* Action buttons */}
              <ClockActionButtons
                employee={employee}
                breakTrackingEnabled={true}
                isProcessing={isProcessing}
                onAction={handleClockAction}
              />
            </div>
          </div>
        );

      case 'result': {
        const isSuccess = lastResult?.success;

        return (
          <div className="flex flex-col items-center justify-center flex-1 p-6">
            <div className={cn(
              'w-24 h-24 rounded-full flex items-center justify-center mb-6',
              isSuccess ? 'bg-green-100 dark:bg-green-500/20' : 'bg-red-100 dark:bg-red-500/20'
            )}>
              {isSuccess ? (
                <CheckCircle className="w-16 h-16 text-green-600 dark:text-green-400" />
              ) : (
                <XCircle className="w-16 h-16 text-red-600 dark:text-red-400" />
              )}
            </div>

            <h2 className={cn(
              'text-2xl font-bold mb-2',
              isSuccess ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
            )}>
              {isSuccess ? 'Success!' : 'Failed'}
            </h2>

            <p className="text-gray-600 dark:text-gray-300 text-lg text-center max-w-md">
              {lastResult?.status_message || lastResult?.error}
            </p>

            {lastResult?.overtime_alert && (
              <div className="mt-4">
                <OvertimeAlert
                  warning={{
                    approaching: lastResult.overtime_alert.approaching,
                    minutes_until_overtime: 0,
                    message: `Worked ${lastResult.overtime_alert.hours_worked} hours today${
                      lastResult.overtime_alert.overtime_minutes
                        ? ` (${lastResult.overtime_alert.overtime_minutes} min overtime)`
                        : ''
                    }`,
                  }}
                />
              </div>
            )}

            <p className="text-gray-400 dark:text-gray-500 text-sm mt-6">
              Returning to main screen...
            </p>
          </div>
        );
      }

      default:
        return null;
    }
  };

  return (
    <div className={cn('flex flex-col flex-1 relative bg-gray-50 dark:bg-background', className)}>
      {renderContent()}
    </div>
  );
}
